import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { features } from '../../features/data';
import { useInView } from '../../../hooks/useInView';

type Item = { slug: string; key: string; tag?: 'AI' };

/** Same order as the Features mega menu. `key` is the label in nav.items.features. */
const ITEMS: Item[] = [
  { slug: 'auto-allocation', key: 'autoAllocation', tag: 'AI' },
  { slug: 'route-planner', key: 'routePlanner', tag: 'AI' },
  { slug: 'branded-tracking', key: 'brandedTracking' },
  { slug: 'proof-of-delivery', key: 'proofOfDelivery' },
  { slug: 'driver-app', key: 'driverApp' },
  { slug: 'ai-agents', key: 'aiAgents', tag: 'AI' },
  { slug: 'cash-management', key: 'cashManagement' },
  { slug: 'reports-analytics', key: 'reportsAnalytics', tag: 'AI' },
];

const ADVANCE_MS = 7000;

/** One feature at a time in a showcase card sized to fit one screen, with a selector for all eight. Auto-advances; pauses on hover. */
export default function PlatformCapabilities() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { ref: sectionRef, inView } = useInView<HTMLElement>(0.3);
  const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const items = ITEMS.map((i) => ({ ...i, data: features[i.slug] })).filter((i) => i.data);
  const count = items.length;

  useEffect(() => {
    if (paused || !inView || reduceMotion || count === 0) return;
    const id = window.setTimeout(() => setActive((i) => (i + 1) % count), ADVANCE_MS);
    return () => window.clearTimeout(id);
  }, [active, paused, inView, reduceMotion, count]);

  const cur = items[active];
  if (!cur) return null;
  const c = cur.data.accentColor ?? '#1769ff';
  const cl = cur.data.accentColorLight ?? '#eef5ff';
  const stat = cur.data.heroStats[0];

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden border-t border-gray-100 bg-gradient-to-b from-white via-slate-50/70 to-white py-12 lg:py-16"
      aria-labelledby="platform-capabilities-title"
    >
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-blue-400/10 blur-3xl" aria-hidden />
      <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-emerald-400/10 blur-3xl" aria-hidden />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mx-auto mb-6 max-w-3xl text-center">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-700">{t('home.platformCapabilities.eyebrow')}</p>
          <h2 id="platform-capabilities-title" className="mt-2 text-3xl font-bold tracking-tight text-gray-900 lg:text-4xl">
            {t('home.platformCapabilities.title')} <span className="text-blue-600">{t('home.platformCapabilities.titleHighlight')}</span>
          </h2>
          <p className="mx-auto mt-2 max-w-2xl text-base leading-relaxed text-gray-600">{t('home.platformCapabilities.subtitle')}</p>
        </div>

        {/* Selector */}
        <div role="tablist" aria-label={t('home.platformCapabilities.eyebrow')} className="mb-5 flex flex-wrap justify-center gap-1.5">
          {items.map((item, i) => {
            const isActive = i === active;
            const ic = item.data.accentColor ?? '#1769ff';
            return (
              <button
                key={item.slug}
                type="button"
                role="tab"
                id={`capability-tab-${item.slug}`}
                aria-selected={isActive}
                aria-controls="capability-panel"
                onClick={() => setActive(i)}
                className={`relative inline-flex items-center gap-1.5 overflow-hidden rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors ${
                  isActive ? 'border-transparent text-white shadow-md' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
                style={isActive ? { backgroundColor: ic } : undefined}
              >
                <i className={`${item.data.icon} text-sm`} aria-hidden />
                {t(`nav.items.features.${item.key}.label`)}
                {isActive && !paused && inView && !reduceMotion && (
                  <span
                    key={active}
                    className="absolute bottom-0 left-0 h-0.5 bg-white/70"
                    style={{ animation: `capability-progress ${ADVANCE_MS}ms linear forwards` }}
                    aria-hidden
                  />
                )}
              </button>
            );
          })}
        </div>

        {/* Showcase card */}
        <div
          id="capability-panel"
          role="tabpanel"
          aria-labelledby={`capability-tab-${cur.slug}`}
          className="relative overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-xl shadow-blue-900/10"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-50"
            style={{ background: `radial-gradient(800px 360px at 85% 50%, ${c}26 0%, transparent 60%)` }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.35]"
            style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #cbd5e1 1px, transparent 0)', backgroundSize: '22px 22px' }}
            aria-hidden
          />

          <div className="relative grid items-center gap-6 p-6 sm:p-8 lg:grid-cols-12 lg:gap-10 lg:p-10">
            <div key={cur.slug} className="animate-fade-in-up lg:col-span-5">
              <div className="flex items-center gap-3">
                <span
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl text-white shadow-md"
                  style={{ backgroundColor: c, boxShadow: `0 10px 24px -10px ${c}` }}
                >
                  <i className={cur.data.icon} aria-hidden />
                </span>
                {cur.tag && (
                  <span className="rounded-md px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider" style={{ backgroundColor: cl, color: c }}>
                    {cur.tag}
                  </span>
                )}
              </div>
              <h3 className="mt-4 text-2xl font-bold leading-tight tracking-tight text-gray-900 sm:text-3xl">
                {t(`nav.items.features.${cur.key}.label`)}
              </h3>
              <p className="mt-3 max-w-md text-[15px] leading-relaxed text-gray-600 [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:4] overflow-hidden">
                {cur.data.description}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-5">
                {stat && (
                  <div className="border-s-[3px] ps-3" style={{ borderColor: c }}>
                    <p className="text-2xl font-bold leading-none text-gray-900">{stat.value}</p>
                    <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500">{stat.label}</p>
                  </div>
                )}
                <Link
                  to={`/features/${cur.slug}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ backgroundColor: c }}
                >
                  {t('home.platformCapabilities.explore')}
                  <i className="ri-arrow-right-up-line" aria-hidden />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="flex h-[260px] items-center justify-center sm:h-[320px] lg:h-[360px]">
                <div className="h-full overflow-hidden rounded-2xl border border-gray-200 bg-slate-50 p-1.5 shadow-[0_24px_50px_-28px_rgba(15,23,42,0.4)]">
                  <img
                    key={cur.data.heroImage.src}
                    src={cur.data.heroImage.src}
                    alt={cur.data.heroImage.alt}
                    width={cur.data.heroImage.width}
                    height={cur.data.heroImage.height}
                    loading={active === 0 ? 'eager' : 'lazy'}
                    className="animate-fade-in-up h-full w-auto max-w-full rounded-xl object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="relative flex items-center justify-between border-t border-gray-200 px-6 py-2 text-xs text-gray-500 sm:px-8">
            <span>
              {String(active + 1).padStart(2, '0')} / {String(count).padStart(2, '0')}
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setActive((i) => (i - 1 + count) % count)}
                aria-label={t('home.platformCapabilities.prev')}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 hover:bg-gray-100"
              >
                <i className="ri-arrow-left-s-line rtl:rotate-180" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                aria-label={paused ? t('home.platformCapabilities.play') : t('home.platformCapabilities.pause')}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 hover:bg-gray-100"
              >
                <i className={paused ? 'ri-play-fill' : 'ri-pause-fill'} aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => setActive((i) => (i + 1) % count)}
                aria-label={t('home.platformCapabilities.next')}
                className="flex h-7 w-7 items-center justify-center rounded-full border border-gray-200 hover:bg-gray-100"
              >
                <i className="ri-arrow-right-s-line rtl:rotate-180" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`@keyframes capability-progress { from { width: 0 } to { width: 100% } }`}</style>
    </section>
  );
}
